"use client";

import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

import { tr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { CalendarProps } from "@/lib/types/calendar.types";

const calendarVariants = cva("", {
  variants: {
    intent: {
      primary: [
        "bg-opacityGray",
        "text-zinc-800",
        "border-zinc-600 border rounded-[10px]",
        "focus:outline-none focus:ring-2 focus:ring-zinc-800 focus:border-transparent",
        "h-[40px]",
      ],
      secondary: ["bg-gray-200", "text-gray-800", "border-gray-400"],
      success: ["bg-green-500", "text-white", "border-transparent"],
      white: [
        "bg-white",
        "text-zinc-800",
        "border-none",
        "focus:outline-none focus:ring-2 focus:ring-zinc-800 focus:border-transparent",
      ],
      transparent: [
        "bg-transparent",
        "border-none",
        "hover:bg-gray-200",
        "text-zinc-800",
        "disabled:text-text-exp",
      ],
      refresh: [
        "bg-green-500/15",
        "text-green-500",
        "border-none",
        "hover:bg-green-500/25",
      ],
    },
    hasLeftIcon: {
      true: "pl-10",
      false: "pl-3",
    },
    hasRightIcon: {
      true: "pr-10 !text-left",
      false: "pr-3",
    },
    hasError: {
      true: "border-red-500 border",
      false: "border-zinc-600 border",
    },
    variant: {
      default:
        "block w-full rounded-lg h-10 py-2 text-sm outline-2 placeholder:text-zinc-600",
      waitlist:
        "block w-full rounded-lg h-14 py-2 text-sm outline-2 placeholder:text-zinc-600 bg-white",
      filter:
        'h-8 placeholder:text-xs text-center w-full rounded-md focus:border-zinc-800 outline-none [&:not(:placeholder-shown)]:border-zinc-800 placeholder:text-zinc-600 [&[type="date"]]:border-input',
    },
  },
  defaultVariants: {
    hasLeftIcon: false,
    hasRightIcon: false,
    hasError: false,
    variant: "default",
  },
});

const FormCalendar = ({
  label,
  className,
  value,
  disabled,
  ...props
}: CalendarProps) => {
  const [openCalendar, setOpenCalendar] = useState(false);

  return (
    <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-10 w-60 flex items-center justify-between bg-transparent border px-3 rounded-lg border-zinc-600/15 text-zinc-800 hover:bg-zinc-600/15",
            { "text-zinc-800": value },
            className
          )}
        >
          {value ? format(value, "dd.MM.yyyy") : <span>Tarih seç</span>}
          <CalendarIcon className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          locale={tr}
          mode="single"
          disabled={disabled}
          selected={value}
          onSelect={(e) => {
            if (typeof e === "undefined") {
              return;
            }
            props.onSelect?.(e);
            setOpenCalendar(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

FormCalendar.displayName = "FormCalendar";

export { FormCalendar, calendarVariants };
